import { useState, useEffect } from "react";
import { Calendar, MapPin, Clock, Users, Minus, Plus, ShoppingCart, Check, Ticket, Heart, Star, ExternalLink, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

import { API } from "../config/api";

// ============================================================================
// DIWALI KUDUMBASANGAMAM 2026
// ============================================================================

// TICKET SALES - Set to false to enable purchases
const TICKETS_DISABLED = false;
const TICKETS_DISABLED_MESSAGE = "Ticket sales are currently closed. Thank you for your interest!";

// Category definitions with prices
const DIWALI_CATEGORIES = [
  { id: "A", name: "Adult", description: "13 & Above", code: "adult", price: 20 },
  { id: "B", name: "Youth", description: "5-12", code: "youth", price: 15 },
  { id: "C", name: "Kids", description: "0-5", code: "kids", price: 0 },
];

export default function DiwaliEventPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, []);

  const fetchEventData = async () => {
    try {
      const eventRes = await fetch(`${API}/events/diwali-2026`);
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      } else {
        setEvent({
          title: "Diwali Kudumbasangamam 2026",
          slug: "diwali-2026",
          description: "Celebrate Diwali with Tatvamasi London Ontario",
          date_time: "2026-11-07T17:00:00",
          venue: "Ilderton Community Centre",
          address: "13168 Ilderton Road, Middlesex Centre, Ilderton, ON N0M 2A0",
          maps_url: "https://www.google.com/maps/search/?api=1&query=Ilderton+Community+Centre+13168+Ilderton+Road+Middlesex+Centre+Ilderton+ON"
        });
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      setEvent({
        title: "Diwali Kudumbasangamam 2026",
        slug: "diwali-2026",
        description: "Celebrate Diwali with Tatvamasi London Ontario",
        date_time: "2026-11-07T17:00:00",
        venue: "Ilderton Community Centre",
        address: "13168 Ilderton Road, Middlesex Centre, Ilderton, ON N0M 2A0",
        maps_url: "https://www.google.com/maps/search/?api=1&query=Ilderton+Community+Centre+13168+Ilderton+Road+Middlesex+Centre+Ilderton+ON"
      });
    } finally {
      setLoading(false);
    }
  };

  const getTicketPrice = (categoryId) => {
    const category = DIWALI_CATEGORIES.find(c => c.id === categoryId);
    return category ? category.price : 0;
  };

  const getTotalPrice = () => {
    let total = 0;
    Object.entries(cart).forEach(([categoryId, qty]) => {
      const price = getTicketPrice(categoryId) * 100;
      total += (price * qty);
    });
    return total;
  };

  const getTotalTickets = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  };

  const updateCart = (categoryId, delta) => {
    setCart(prev => {
      const current = prev[categoryId] || 0;
      const newQty = Math.max(0, Math.min(current + delta, 20));
      if (newQty === 0) {
        const { [categoryId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [categoryId]: newQty };
    });
  };

  const handleCheckout = async () => {
    if (TICKETS_DISABLED) {
      toast.error("Ticket sales are currently closed");
      return;
    }
    if (getTotalTickets() === 0) {
      toast.error("Please select at least one ticket");
      return;
    }
    if (!customerInfo.name || !customerInfo.email) {
      toast.error("Please enter your name and email");
      return;
    }

    setCheckingOut(true);

    try {
      const ticketsData = [];
      Object.entries(cart).forEach(([categoryId, qty]) => {
        if (qty > 0) {
          const category = DIWALI_CATEGORIES.find(c => c.id === categoryId);
          const ticketPrice = getTicketPrice(categoryId);
          ticketsData.push({
            slot_id: "DIWALI",
            slot_name: "Diwali Kudumbasangamam 2026",
            slot_time: "5:00 PM - 9:00 PM",
            category_id: categoryId,
            category_name: category?.name || "Ticket",
            description: category?.description || "",
            price_cents: ticketPrice * 100,
            quantity: qty
          });
        }
      });

      let eventId = event?.id;
      if (!eventId) {
        const evRes = await fetch(`${API}/events/diwali-2026`);
        if (evRes.ok) {
          const evData = await evRes.json();
          eventId = evData.id;
        }
      }

      const response = await fetch(`${API}/payments/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: eventId || "diwali-2026",
          tickets_data: ticketsData,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone || "",
          origin_url: window.location.origin,
          total_amount: getTotalPrice()
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error("No checkout URL received");
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Failed to start checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kerala-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50" data-testid="diwali-event-page">
      {/* Hero Section with Diwali Flyer */}
      <section className="relative" style={{ backgroundColor: "#1a1a2e" }}>
        {/* Full-width Diwali Flyer Banner */}
        <div className="w-full">
          <img 
            src="/diwali-flyer-1.png" 
            alt="Diwali Kudumbasangamam 2026" 
            className="w-full h-auto max-h-[80vh] mx-auto object-contain"
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 md:py-12">
        <div className="section-container">
          <div className="max-w-2xl mx-auto">
            {/* Ticket Selection Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              {/* Header */}
              <div className="bg-kerala-green text-white p-6">
                <div className="flex items-center gap-3">
                  <Ticket className="w-6 h-6" />
                  <div>
                    <h2 className="font-heading text-xl font-semibold">
                      {TICKETS_DISABLED ? "Ticket Sales Closed" : "Select Your Tickets"}
                    </h2>
                    <p className="text-sm text-white/80">
                      {TICKETS_DISABLED ? "Thank you for your interest" : "Choose the number of tickets per category"}
                    </p>
                  </div>
                </div>
              </div>

              {TICKETS_DISABLED ? (
                /* Tickets Disabled Message */
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Ticket className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-heading text-xl text-gray-700 mb-2">Ticket Sales Closed</h3>
                  <p className="text-gray-500">{TICKETS_DISABLED_MESSAGE}</p>
                  
                  {/* Event Info */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-start gap-3 text-sm text-gray-600 justify-center">
                      <MapPin className="w-5 h-5 text-kerala-green flex-shrink-0 mt-0.5" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{event?.venue || "Ilderton Community Centre"}</p>
                        <p>{event?.address || "13168 Ilderton Road, Middlesex Centre, Ilderton, ON N0M 2A0"}</p>
                        <a 
                          href={event?.maps_url || "https://www.google.com/maps/search/?api=1&query=Ilderton+Community+Centre+13168+Ilderton+Road"} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-kerala-green hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          View on Google Maps <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Ticket Selection */
                <div className="p-6 space-y-4">
                  {DIWALI_CATEGORIES.map((category) => (
                    <div 
                      key={category.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-kerala-green/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-kerala-green/10 flex items-center justify-center">
                          <span className="font-bold text-kerala-green">{category.id}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{category.name}</p>
                          <p className="text-sm text-gray-500">{category.description} - ${category.price.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-full"
                          onClick={() => updateCart(category.id, -1)}
                          disabled={!cart[category.id]}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold">{cart[category.id] || 0}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-full bg-kerala-green text-white hover:bg-kerala-green/90"
                          onClick={() => updateCart(category.id, 1)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Venue Info */}
                  <div className="bg-gray-50 p-4 border-t border-gray-100 rounded-xl">
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <MapPin className="w-5 h-5 text-kerala-green flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">{event?.venue || "Ilderton Community Centre"}</p>
                        <p>{event?.address || "13168 Ilderton Road, Middlesex Centre, Ilderton, ON N0M 2A0"}</p>
                        <a 
                          href={event?.maps_url || "https://www.google.com/maps/search/?api=1&query=Ilderton+Community+Centre+13168+Ilderton+Road"} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-kerala-green hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          View on Google Maps <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              {!TICKETS_DISABLED && (
                <div className="bg-kerala-green text-white p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <ShoppingCart className="w-5 h-5" />
                    <h3 className="font-heading text-lg">Order Summary</h3>
                  </div>

                  {getTotalTickets() > 0 ? (
                    <div className="space-y-4">
                      {/* Line Items */}
                      {Object.entries(cart).map(([categoryId, qty]) => {
                        if (qty <= 0) return null;
                        const category = DIWALI_CATEGORIES.find(c => c.id === categoryId);
                        const lineTotal = category.price * qty;
                        return (
                          <div key={categoryId} className="flex justify-between text-sm">
                            <span>{category.name}</span>
                            <span>{qty} x ${category.price.toFixed(2)} = ${lineTotal.toFixed(2)}</span>
                          </div>
                        );
                      })}

                      {/* Total */}
                      <div className="pt-3 border-t border-white/20">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">Total</span>
                          <span className="text-2xl font-bold">${(getTotalPrice() / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="pt-4 space-y-3">
                        <h4 className="font-medium">Your Details</h4>
                        <div>
                          <Label htmlFor="name" className="text-white/80 text-sm">Full Name *</Label>
                          <Input
                            id="name"
                            type="text"
                            placeholder="Enter your full name"
                            className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            value={customerInfo.name}
                            onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-white/80 text-sm">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="your@email.com"
                            className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            value={customerInfo.email}
                            onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-white/80 text-sm">Phone (Optional)</Label>
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="Your phone number"
                            className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            value={customerInfo.phone}
                            onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                          />
                        </div>
                      </div>

                      {/* Checkout Button */}
                      <Button
                        className="w-full bg-kerala-gold hover:bg-kerala-gold/90 text-kerala-green font-semibold py-6 text-lg mt-4"
                        onClick={handleCheckout}
                        disabled={checkingOut || getTotalTickets() === 0}
                      >
                        {checkingOut ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-kerala-green mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Proceed to Payment
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Ticket className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-white/70">No tickets selected</p>
                      <p className="text-sm text-white/50">Choose your tickets to continue</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-8 bg-kerala-cream/50">
        <div className="section-container">
          <div className="max-w-2xl mx-auto text-center">
            <Heart className="w-8 h-8 text-kerala-terracotta mx-auto mb-3" />
            <h3 className="font-heading text-xl text-kerala-green mb-2">Support This Event</h3>
            <p className="text-gray-600 mb-4">Help make Diwali Kudumbasangamam 2026 unforgettable</p>
            <Link to="/sponsors">
              <Button variant="outline" className="border-kerala-green text-kerala-green hover:bg-kerala-green hover:text-white">
                Become a Sponsor
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
